import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { appServices } from '@/data/AppServices';
import type { MasteryFilter, Vocabulary, VocabularyFilter } from '@/domain/Models';

export function VocabularyListPage() {
  const [filter, setFilter] = useState<VocabularyFilter>({
    query: '',
    mastery: 'ALL',
    dueOnly: false,
    favoritesOnly: false,
  });
  const [words, setWords] = useState<Vocabulary[]>([]);
  const [queue, setQueue] = useState({ newCount: 0, reviewCount: 0 });
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const reload = useCallback(async () => {
    try {
      const [list, today] = await Promise.all([
        appServices.vocabulary.list(filter),
        appServices.vocabulary.todayQueue(),
      ]);
      setWords(list);
      setQueue(today);
      setError(null);
    } catch (e) {
      setError(String(e));
    }
  }, [filter]);

  useEffect(() => {
    void reload();
  }, [reload]);

  function tapMastery(next: MasteryFilter) {
    setFilter((f) => ({ ...f, mastery: f.mastery === next ? 'ALL' : next }));
  }

  return (
    <div className="page stack">
      <div>
        <strong>今日词汇</strong>
        <div className="muted">
          新内容 {queue.newCount} · 到期 {queue.reviewCount}
        </div>
      </div>
      <button
        type="button"
        disabled={queue.newCount + queue.reviewCount === 0}
        onClick={() => navigate('/vocabulary/practice')}
      >
        开始背单词
      </button>
      {queue.newCount + queue.reviewCount === 0 && (
        <div className="muted">今天的自动学习任务已完成，仍可在下方浏览词库或收藏。</div>
      )}
      <input
        value={filter.query}
        onChange={(e) => setFilter((f) => ({ ...f, query: e.target.value }))}
        placeholder="搜索单词 / 释义"
        aria-label="搜索单词或释义"
      />
      <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
        <button
          type="button"
          className={`filter-chip${filter.mastery === 'NOT_STARTED' ? ' active' : ''}`}
          onClick={() => tapMastery('NOT_STARTED')}
        >
          未学
        </button>
        <button
          type="button"
          className={`filter-chip${filter.mastery === 'LEARNING' ? ' active' : ''}`}
          onClick={() => tapMastery('LEARNING')}
        >
          学习中
        </button>
        <button
          type="button"
          className={`filter-chip${filter.mastery === 'MASTERED' ? ' active' : ''}`}
          onClick={() => tapMastery('MASTERED')}
        >
          已掌握
        </button>
        <button
          type="button"
          className={`filter-chip${filter.dueOnly ? ' active' : ''}`}
          onClick={() => setFilter((f) => ({ ...f, dueOnly: !f.dueOnly }))}
        >
          到期
        </button>
        <button
          type="button"
          className={`filter-chip${filter.favoritesOnly ? ' active' : ''}`}
          onClick={() => setFilter((f) => ({ ...f, favoritesOnly: !f.favoritesOnly }))}
        >
          收藏
        </button>
      </div>
      {error && <div className="error-box">{error}</div>}
      {words.map((w) => (
        <section
          key={w.id}
          className="card"
          style={{ cursor: 'pointer' }}
          onClick={() => navigate(`/vocabulary/${encodeURIComponent(w.id)}`)}
        >
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div className="stack" style={{ gap: 4, flex: 1 }}>
              <div>
                <strong>{w.term}</strong>
                {w.itemKind === 'PHRASE' && <span className="chip" style={{ marginLeft: 8 }}>短语</span>}
                {w.ipa && <span className="muted"> {w.ipa}</span>}
              </div>
              <div>{w.chineseGloss}</div>
              <div className="muted">
                {w.itemKind === 'PHRASE' ? '短语 · ' : ''}
                {w.cefrLevel ? `${w.cefrLevel} · ` : ''}
                {w.topic}
                {w.started ? ` · 复习 ${w.reps} 次` : ' · 未开始'}
              </div>
            </div>
            <button
              type="button"
              className="icon-btn"
              aria-label={w.favorite ? `取消收藏 ${w.term}` : `收藏 ${w.term}`}
              onClick={(e) => {
                e.stopPropagation();
                void (async () => {
                  await appServices.vocabulary.setFavorite(w.id, !w.favorite);
                  await reload();
                })();
              }}
            >
              {w.favorite ? '♥' : '♡'}
            </button>
          </div>
        </section>
      ))}
      {words.length === 0 && <div className="muted">没有符合条件的词</div>}
    </div>
  );
}
